import {
    CommandLineIcon,
    CheckCircleIcon,
    ClockIcon,
    DocumentTextIcon
} from '@heroicons/react/24/outline'
import { useState } from 'react'
import type { INotebookContent } from '@jupyterlab/nbformat'
import { useTar } from '@/modules'
import { ChallengeService } from '@duck/api-definitions'
import { CompletedChallenge } from '../FeedbackDialog'
import { RelativeDateTime } from '../datetime/RelativeDateTime'
import { WysiwygEditor } from '../WysiwygEditor/WysiwygEditor'
import { ReadOnlyPythonNotebook } from '../PythonNotebook/ReadOnlyPythonNotebook'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    Button
} from '@duck/ui'
import { DevPrint } from '@components/DevPrint'
import { formatWorkTime } from '@/utils/time.utils'
import { AllowedWeaponsPills } from '@components/AllowedWeaponsPills'

interface CompletedAssignmentCardProps {
    completedChallenge: CompletedChallenge
    onShowResults: (challenge: CompletedChallenge) => void
}

export const CompletedAssignmentCard = ({
    completedChallenge,
    onShowResults
}: CompletedAssignmentCardProps) => {
    const { t } = useTar()
    const { challenge, task, icon } = completedChallenge
    const IconComponent = icon || CommandLineIcon
    const [isSolutionDialogOpen, setIsSolutionDialogOpen] = useState(false)
    const isFeedbackAvailable = challenge.feedbackStatus === ChallengeService.FeedbackStatusEnum.Processed && challenge.feedbackText

    return (
        <>
            <div
                className="flex flex-col flex-1 bg-card rounded-lg shadow-sm border border-border p-6 hover:shadow-md transition-shadow duration-200"
            >
                {/* Assignment Icon & Category */}
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-success/10 rounded-lg">
                            <IconComponent className="w-6 h-6 text-success" />
                        </div>
                        <AllowedWeaponsPills
                            allowedWeapons={task.allowedWeapons}
                            chosenWeapon={challenge.workState?.chosenWeapon}
                        />
                    </div>
                </div>

                {/* Assignment Title */}
                <h2 className="text-xl font-semibold text-foreground mb-3 h-12 leading-6 flex items-start">
                    <span className="line-clamp-2">
                        {task.name}
                    </span>
                </h2>

                <DevPrint label="challengeId" value={challenge.id} />
                {/* Assignment Description */}
                <p className="text-muted-foreground text-sm mb-4 leading-relaxed line-clamp-1">
                    {task.teaser}
                </p>

                {/* Submission Status */}
                <div className="flex items-center text-success text-xs mb-4">
                    <CheckCircleIcon className="w-4 h-4 me-1" />
                    <span className="font-medium">{t('challenges.completedStatus')}</span>
                    <span className="mx-2">•</span>
                    <span>{t('challenges.finishedIn', { time: formatWorkTime(challenge.workTime, t) })}</span>
                </div>

                {/* End Time */}
                {challenge.endTime && (
                    <div className="flex items-center text-muted-foreground text-xs mb-6">
                        <ClockIcon className="w-4 h-4 me-1" />
                        <RelativeDateTime isoDateString={challenge.endTime} makeLabel={(time) => t('userFeedback.submittedTIME', { time })} />
                    </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3 flex-1 items-end">
                    <Button
                        variant="default"
                        className="flex-1 flex items-center justify-center gap-2"
                        onClick={() => setIsSolutionDialogOpen(true)}
                    >
                        <DocumentTextIcon className="w-4 h-4" />
                        <span>{t('challenges.viewSubmission')}</span>
                    </Button>
                    {isFeedbackAvailable && (
                        <Button
                            variant="outline"
                            className="flex-1 flex items-center justify-center gap-2"
                            onClick={() => onShowResults(completedChallenge)}
                        >
                            <CheckCircleIcon className="w-4 h-4" />
                            <span>{t('challenges.viewResults')}</span>
                        </Button>
                    )}
                </div>
            </div>

            {/* Solution Dialog */}
            <Dialog open={isSolutionDialogOpen} onOpenChange={setIsSolutionDialogOpen}>
                <DialogContent className="max-w-4xl max-h-[80vh] flex flex-col">
                    <DialogHeader>
                        <DialogTitle>{t('challenges.submission')}</DialogTitle>
                    </DialogHeader>
                    <div className="flex-1 overflow-auto border border-border rounded-md bg-background min-h-[300px]">
                        {challenge.workState?.chosenWeapon === ChallengeService.TaskWeaponsOfChoiceEnum.Python ? (
                            <ReadOnlyPythonNotebook
                                notebook={challenge.workState?.jNotebook as INotebookContent | undefined}
                            />
                        ) : (
                            <WysiwygEditor
                                readOnly
                                content={challenge.workState?.solution || ''}
                                startingCharts={challenge.workState?.charts || {}}
                            />
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </>
    )
}
